import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, ArrowLeft, Image as ImageIcon, Star, CheckCircle, X, Sunrise, Sunset, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';
import OptimizedImage from '../components/OptimizedImage';
import TempleCard from '../components/TempleCard';
import { getTempleById, submitTempleRating, getRelatedTemples } from '../services/api';
import { generateTempleSchema, generateBreadcrumbSchema, generateFAQSchema } from '../utils/schemaGenerator';

const TempleDetails = () => {
  const { id } = useParams();
  const [temple, setTemple] = useState(null);
  const [relatedTemples, setRelatedTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Rating states
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingSuccess, setRatingSuccess] = useState(false);

  // Gallery zoom state
  const [selectedImage, setSelectedImage] = useState(null);

  // FAQ state
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  useEffect(() => {
    const fetchTemple = async () => {
      setLoading(true);
      const data = await getTempleById(id);
      setTemple(data);
      if (data) {
        const related = await getRelatedTemples(id, 4);
        setRelatedTemples(related);
      }
      setLoading(false);
    };
    fetchTemple();
  }, [id]);

  const handleRate = async (rateValue) => {
    if (userRating > 0 || submittingRating) return; // Prevent multiple submissions
    setSubmittingRating(true);
    setUserRating(rateValue);
    
    // Call the mock API from api.js
    const result = await submitTempleRating(temple.id, rateValue);
    if (result.success) {
      setRatingSuccess(true);
      setTemple(prev => {
        const currentReviews = Number(prev.reviews) || 0;
        const currentRating = Number(prev.rating) || 0;
        const newReviews = currentReviews + 1;
        const newRating = ((currentRating * currentReviews) + rateValue) / newReviews;
        return { ...prev, rating: parseFloat(newRating.toFixed(1)), reviews: newReviews };
      });
    }
    setSubmittingRating(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600"></div>
      </div>
    );
  }

  if (!temple) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 text-center px-4">
        <h2 className="text-3xl font-bold text-stone-800 mb-4">मंदिर नहीं मिला</h2>
        <p className="text-stone-600 mb-8">आप जिस मंदिर को खोज रहे हैं वह मौजूद नहीं है या उपलब्ध नहीं है।</p>
        <Link to="/temples" className="px-6 py-3 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition">
          वापस जाएँ
        </Link>
      </div>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : null;
  const templeSchema = generateTempleSchema(temple, currentUrl);
  
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Temples', url: '/temples' },
    { name: temple.name, url: `/temple/${temple.id || temple.slug || id}` }
  ]);

  // Build FAQ Schema fallbacks from temple data
  let faqData = [];
  let displayFaqs = [];
  if (temple.faqs && temple.faqs.length > 0) {
    displayFaqs = temple.faqs.filter(f => f.status === 'active');
    faqData = displayFaqs.map(f => ({
      question: f.question,
      answer: f.answer.replace(/<[^>]+>/g, '') // Standard strip HTML for JSON-LD compliance
    }));
  } else {
    // Auto-generate fallbacks
    if (temple.morning_timing || temple.evening_timing) {
      faqData.push({
        question: `What are the darshan timings for ${temple.name}?`,
        answer: `The temple timings are: Morning - ${temple.morning_timing || 'Open'}, Evening - ${temple.evening_timing || 'Open'}.`
      });
    }
    if (temple.location) {
      faqData.push({
        question: `Where is ${temple.name} located?`,
        answer: `${temple.name} is located at ${temple.location}, Bareilly, Uttar Pradesh, India.`
      });
    }
    if (temple.seo_description || temple.description) {
      faqData.push({
        question: `Why is ${temple.name} famous?`,
        answer: temple.seo_description || `It is one of the most prominent Nath temples in Bareilly's spiritual round.`
      });
    }
    displayFaqs = faqData.map(f => ({ question: f.question, answer: f.answer }));
  }
  
  const faqSchemaObj = generateFAQSchema(faqData);
  const rawSchemas = [templeSchema, breadcrumbSchema, faqSchemaObj].filter(Boolean);

  return (
    <div className="bg-white min-h-screen pb-20">
      <SEO 
        title={temple.seo_title || temple.name}
        description={temple.seo_description || temple.description}
        keywords={temple.seo_keywords || `${temple.name}, temple, bareilly, nathnagri, pilgrimage`}
        image={temple.seo_image || temple.image}
        url={currentUrl || `https://nathnagribareilly.com/temple/${id}`}
        schema={rawSchemas}
      />
      
      {/* Hero Header */}
      <div className="relative h-[60vh] w-full">
        <OptimizedImage 
          src={temple.image} 
          alt={temple.name} 
          title={temple.seo_title || temple.name}
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent"></div>
        <div className="absolute top-8 left-8">
          <Link 
            to="/temples"
            className="inline-flex items-center text-white/80 hover:text-white bg-black/20 hover:bg-black/40 backdrop-blur-md px-4 py-2 rounded-full transition-all"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> सभी मंदिर
          </Link>
        </div>
        <div className="absolute bottom-10 left-0 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg"
            >
              {temple.name}
            </motion.h1>
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="flex flex-wrap items-center gap-4 text-white/90"
            >
              <div className="flex items-center bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-lg">
                <MapPin className="w-5 h-5 text-saffron-400 mr-2" />
                <span className="font-medium text-sm">{temple.location}</span>
              </div>
              <div className="flex items-center bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-lg space-x-4">
                <div className="flex items-center">
                   <Sunrise className="w-5 h-5 text-saffron-400 mr-2" />
                   <span className="font-medium text-sm">{temple.morning_timing}</span>
                </div>
                <div className="flex items-center border-l border-white/30 pl-4">
                   <Sunset className="w-5 h-5 text-saffron-400 mr-2" />
                   <span className="font-medium text-sm">{temple.evening_timing}</span>
                </div>
              </div>
              <div className="flex items-center bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-lg">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 mr-2" />
                <span className="font-medium text-sm">{temple.rating} / 5 ({temple.reviews} समीक्षाएं)</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl shadow-stone-200/50 border border-stone-100">
              <h2 className="text-2xl font-bold text-stone-800 mb-6">मंदिर के बारे में</h2>
              <div 
                className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed quill-content [&>p]:mb-6 [&>div]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mb-4"
                dangerouslySetInnerHTML={{ __html: (temple.full_description || `<p>${temple.description}</p>`).replace(/&nbsp;/g, ' ') }}
              />
              
              {/* Embedded Ad inside content body */}
              <div className="my-8">
                <AdBanner placement="in-content" pageName="temple_detail" />
              </div>

              {/* FREQUENTLY ASKED QUESTIONS */}
              {displayFaqs && displayFaqs.length > 0 && (
                <div className="mt-12">
                   <h3 className="text-2xl font-bold text-stone-800 mb-6">Frequently Asked Questions</h3>
                   <div className="space-y-4">
                     {displayFaqs.slice(0, 5).map((faq, index) => (
                       <div key={index} className="bg-stone-50 border border-stone-200 rounded-2xl overflow-hidden hover:border-saffron-300 transition-colors">
                         <button 
                           onClick={() => toggleFaq(index)} 
                           className="w-full text-left px-6 py-4 flex justify-between items-center focus:outline-none"
                         >
                           <span className="font-bold text-stone-800 pr-8">{faq.question}</span>
                           <motion.div animate={{ rotate: openFaqIndex === index ? 180 : 0 }} className="text-saffron-600 flex-shrink-0 bg-saffron-100 p-1.5 rounded-full">
                             <ChevronDown className="w-5 h-5" />
                           </motion.div>
                         </button>
                         <AnimatePresence>
                           {openFaqIndex === index && (
                             <motion.div
                               initial={{ height: 0, opacity: 0 }}
                               animate={{ height: 'auto', opacity: 1 }}
                               exit={{ height: 0, opacity: 0 }}
                               transition={{ duration: 0.3, ease: 'easeInOut' }}
                               className="overflow-hidden"
                             >
                               <div className="px-6 pb-5 pt-0 text-stone-600 leading-relaxed prose prose-stone max-w-none text-sm md:text-base [&>a]:text-saffron-600 [&>a]:font-bold"
                                    dangerouslySetInnerHTML={{ __html: faq.answer }}>
                               </div>
                             </motion.div>
                           )}
                         </AnimatePresence>
                       </div>
                     ))}
                   </div>
                </div>
              )}

              {/* Rating Submission Widget */}
              <div className="mt-10 p-6 bg-stone-50 rounded-2xl border border-stone-200">
                <h3 className="text-lg font-bold text-stone-800 mb-2">अपना अनुभव रेट करें</h3>
                <p className="text-sm text-stone-500 mb-4">अन्य भक्तों को इस मंदिर के बारे में जानने में मदद करें।</p>
                
                {ratingSuccess ? (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg w-fit"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">आपकी रेटिंग सफलतापूर्वक सबमिट कर दी गई! धन्यवाद।</span>
                  </motion.div>
                ) : (
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        disabled={submittingRating}
                        onClick={() => handleRate(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star 
                          className={`w-8 h-8 ${
                            (hoverRating || userRating) >= star 
                              ? 'text-yellow-400 fill-yellow-400' 
                              : 'text-stone-300'
                          } ${submittingRating ? 'opacity-50 cursor-not-allowed' : ''}`} 
                        />
                      </button>
                    ))}
                    {submittingRating && <span className="text-sm text-stone-400 ml-4 animate-pulse">सबमिट किया जा रहा है...</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Dynamic Gallery */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl shadow-stone-200/50 border border-stone-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-stone-800">गैलरी (Gallery)</h2>
                <ImageIcon className="text-stone-400 w-6 h-6" />
              </div>
              
              {(() => {
                // Ensure we have an array to work with
                let imgArray = [];
                if (temple.images) {
                  if (typeof temple.images === 'string') {
                    try { imgArray = JSON.parse(temple.images); } catch(e) { }
                  } else if (Array.isArray(temple.images)) {
                    imgArray = temple.images;
                  }
                }
                
                return imgArray && imgArray.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {imgArray.map((imgObj, index) => {
                      const url = typeof imgObj === 'string' ? imgObj : imgObj.url;
                      const altText = typeof imgObj === 'object' && imgObj.alt ? imgObj.alt : `${temple.name} gallery ${index + 1}`;
                      const titleText = typeof imgObj === 'object' && imgObj.title ? imgObj.title : '';
                      
                      if (!url) return null; // Skip invalid entries
                      
                      return (
                        <div 
                          key={index} 
                          onClick={() => setSelectedImage(url)}
                          className="aspect-square bg-stone-100 rounded-2xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                          title={titleText}
                        >
                          <OptimizedImage 
                            src={url} 
                            alt={altText}
                            title={titleText}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e)=>{e.target.src="https://via.placeholder.com/400x400?text=No+Img"}}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-stone-500 italic text-center py-6">इस मंदिर के लिए कोई और चित्र उपलब्ध नहीं हैं।</p>
                );
              })()}
            </div>
          </div>

          {/* Sidebar / Map */}
          <div className="space-y-8">
            <AdBanner placement="sidebar" pageName="temple_detail" />

            <div className="bg-stone-50 rounded-3xl p-8 border border-stone-200">
              <h3 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
                <MapPin className="mr-2 text-saffron-600" /> स्थान (Location)
              </h3>
              <div className="aspect-video bg-stone-200 rounded-xl overflow-hidden mb-4 relative shadow-inner">
                <iframe 
                  title={`Map for ${temple.name}`}
                  src={temple.map_url && temple.map_url.includes('embed') ? temple.map_url : `https://www.google.com/maps?q=${encodeURIComponent(temple.name + " " + temple.location)}&hl=es;z=14&output=embed`}
                  className="w-full h-full border-0 absolute inset-0" 
                  loading="lazy"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-stone-600 text-sm">{temple.location}</p>
              <a 
                href={temple.map_url && temple.map_url.length > 5 ? temple.map_url : `https://maps.google.com/?q=${encodeURIComponent(temple.name + " " + temple.location)}`}
                target="_blank" rel="noreferrer"
                className="mt-6 w-full block text-center bg-white border-2 border-saffron-600 text-saffron-600 hover:bg-saffron-50 font-semibold py-3 rounded-xl transition-colors"
              >
                दिशा-निर्देश प्राप्त करें (Get Directions)
              </a>
            </div>

            <div className="bg-gradient-to-br from-saffron-600 to-saffron-700 rounded-3xl p-8 text-white shadow-xl shadow-saffron-600/20">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Clock className="mr-2" /> यात्रा विवरण (Visit Details)
              </h3>
              <div className="space-y-4">
                <div className="pb-4 border-b border-white/20 flex flex-col space-y-3">
                  <div>
                    <p className="text-white/80 text-sm mb-1 flex items-center"><Sunrise className="w-4 h-4 mr-1"/> प्रातः दर्शन (Morning)</p>
                    <p className="font-semibold">{temple.morning_timing}</p>
                  </div>
                  <div>
                    <p className="text-white/80 text-sm mb-1 flex items-center"><Sunset className="w-4 h-4 mr-1"/> संध्या दर्शन (Evening)</p>
                    <p className="font-semibold">{temple.evening_timing}</p>
                  </div>
                </div>
                <div>
                  <p className="text-white/80 text-sm mb-1">प्रवेश शुल्क (Entry Fee)</p>
                  <p className="font-semibold">सभी के लिए निःशुल्क</p>
                </div>
              </div>
              <p className="mt-6 text-sm text-saffron-100 bg-black/10 p-4 rounded-xl">
                कृपया शालीन कपड़े पहनें और मंदिर परिसर के अंदर चमड़े की वस्तुएं ले जाने से बचें। मुख्य गर्भगृह में फोटोग्राफी प्रतिबंधित हो सकती है।
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Nearby Temples Section */}
      {relatedTemples.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 pb-12 border-t border-stone-200 pt-12">
          <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight mb-8">आस-पास के अन्य मंदिर (Nearby Temples)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedTemples.map((rt, index) => (
              <TempleCard key={`related-${rt.id}`} temple={rt} index={index} compact={true} />
            ))}
          </div>
        </section>
      )}

      {/* Fullscreen Image Zoom Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white/50 hover:text-white bg-white/10 p-2 rounded-full transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          
          <motion.img 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            src={selectedImage} 
            alt="Zoomed view" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking the image itself
          />
        </div>
      )}
    </div>
  );
};

export default TempleDetails;
